import React, { ReactElement, useMemo } from 'react'
import {
    GetFormulaSheetEntriesResponse,
    FormulaSheetEntry,
} from 'common/src/formulaSheet/types'
import styles from './FormulaSheetHTMLMule.module.scss'
import { useRecoilValue } from 'recoil'
import { MagicTextBoxReadOnly } from 'magic-text-box-lexical/src/components/MagicTextBoxReadOnly/MagicTextBoxReadOnly'
import { frontendDisplayedCourseSelector } from '@/atoms/auth'
import { courseInfoAtom } from '@/atoms/courseInfo'
import { getSignedUrlForS3Key } from '@/api/cb/s3'
import {
    accessUniversalContentLinkNoOp,
    mtbReadOnlyOptionsNoOp,
    practiceProblemLinkPropsNoOp,
} from '@/utils/mtbNoOps'
import { useLogger } from '@/hooks/useLogger'
import { groupFormulasByLeafNode } from 'common/src/formulaSheet/utils'
import {
    hashedContentPathsInCanonicalOrderSelector,
    sectionTitleByHashedContentPathSelector,
} from '@/atoms/contentTreeDerived'

export const FormulaSheetHTMLMule: React.FC<GetFormulaSheetEntriesResponse> = (
    props
): ReactElement => {
    const courseName = useRecoilValue(frontendDisplayedCourseSelector)
    const courseInfo = useRecoilValue(courseInfoAtom(courseName))
    const canonicalHashedPaths = useRecoilValue(
        hashedContentPathsInCanonicalOrderSelector
    )
    const sectionTitles = useRecoilValue(
        sectionTitleByHashedContentPathSelector
    )
    const logger = useLogger(FormulaSheetHTMLMule.name)

    const groupedFormulas = useMemo(() => {
        return groupFormulasByLeafNode(props.formulaSheetEntries)
    }, [props.formulaSheetEntries])

    // Sort grouped formulas by canonical content tree order
    const sortedHashedPaths = useMemo(
        () =>
            canonicalHashedPaths.filter((hashedPath) =>
                groupedFormulas.assignedByLeafNode.has(hashedPath)
            ),
        [canonicalHashedPaths, groupedFormulas]
    )
    const validHashedPaths = useMemo(
        () => new Set(canonicalHashedPaths),
        [canonicalHashedPaths]
    )
    const unassignedAndStaleFormulas = useMemo(
        () =>
            props.formulaSheetEntries.filter(
                (entry) =>
                    !entry.hashedContentPath ||
                    !validHashedPaths.has(entry.hashedContentPath)
            ),
        [props.formulaSheetEntries, validHashedPaths]
    )

    const renderFormulaEntry = (formulaSheetEntry: FormulaSheetEntry) => (
        <div
            className={`${styles.formulaSheetEntry} ${
                formulaSheetEntry.shouldForcePageBreakBefore
                    ? styles.shouldForcePageBreakBefore
                    : ''
            }`}
            key={formulaSheetEntry.id}
        >
            <div className={styles.title} key={formulaSheetEntry.id}>
                {formulaSheetEntry.title}
            </div>
            {formulaSheetEntry.description && (
                <div className={styles.description} key={formulaSheetEntry.id}>
                    {formulaSheetEntry.description}
                </div>
            )}
            <MagicTextBoxReadOnly
                initialEditorState={formulaSheetEntry.formulaEditorState}
                practiceProblemLinkProps={practiceProblemLinkPropsNoOp}
                getPreSignedUrlForS3Key={getSignedUrlForS3Key}
                onError={(error) =>
                    logger.error(
                        `Error occurred in formula sheet html mule: ${error}`
                    )
                }
                options={mtbReadOnlyOptionsNoOp}
                accessUniversalContentLink={accessUniversalContentLinkNoOp}
            />
        </div>
    )

    return (
        <div className={styles.mule}>
            <div
                className={styles.overallTitle}
            >{`${courseInfo.prettyName} Formula Sheet`}</div>
            <div className={styles.body}>
                {/* Render grouped formulas by leaf node in canonical order */}
                {sortedHashedPaths.map((hashedContentPath) => {
                    const formulas =
                        groupedFormulas.assignedByLeafNode.get(
                            hashedContentPath
                        )
                    if (!formulas) return null

                    return (
                        <div key={hashedContentPath} className={styles.section}>
                            <div className={styles.sectionHeader}>
                                {sectionTitles.get(hashedContentPath) ??
                                    hashedContentPath}
                            </div>
                            {formulas.map(renderFormulaEntry)}
                        </div>
                    )
                })}

                {/* Render unassigned formulas at the end */}
                {unassignedAndStaleFormulas.length > 0 && (
                    <div className={styles.section}>
                        <div className={styles.sectionHeader}>
                            Unassigned Formulas
                        </div>
                        {unassignedAndStaleFormulas.map(renderFormulaEntry)}
                    </div>
                )}
            </div>
        </div>
    )
}
